import { keys } from "@/lib/i18n/portal";
import type { useTranslations } from "@/lib/i18n/intl";
import type { CouponView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import {
  couponStatusTone,
  formatCouponDate,
  isCouponExpired,
  type CouponStatusTone,
} from "@/modules/coupons/lib/coupon-display";

type Translate = ReturnType<typeof useTranslations>;

/** True while an automatic discount's start date is still in the future. */
export function isDiscountScheduled(coupon: CouponView): boolean {
  if (!coupon.startsAt) return false;
  const at = new Date(coupon.startsAt).getTime();
  return !Number.isNaN(at) && at > Date.now();
}

/** A discount is "live" when active, started, and not expired. */
export function isDiscountLive(coupon: CouponView): boolean {
  return (
    coupon.active && !isDiscountScheduled(coupon) && !isCouponExpired(coupon)
  );
}

/**
 * Status tone for the discount card: a "Scheduled" state layered on top of the
 * shared coupon tones (active / inactive / expired).
 */
export function discountStatusTone(
  coupon: CouponView,
  t: Translate,
): CouponStatusTone {
  if (
    coupon.active &&
    isDiscountScheduled(coupon) &&
    !isCouponExpired(coupon)
  ) {
    return {
      label: t(keys.discounts.statusScheduled),
      dot: "#2563EB",
      bg: "color-mix(in oklab, #2563EB 12%, transparent)",
      fg: "#1D4ED8",
    };
  }
  return couponStatusTone(coupon, t);
}

/** Schedule line for the card: "Scheduled {date}" / "Live" / expiry text. */
export function discountScheduleText(
  coupon: CouponView,
  locale: Locale,
  t: Translate,
): string {
  if (isDiscountScheduled(coupon) && coupon.startsAt) {
    return t(keys.discounts.scheduleStarts, {
      date: formatCouponDate(coupon.startsAt, locale),
    });
  }
  if (coupon.expiresAt) {
    const dateStr = formatCouponDate(coupon.expiresAt, locale);
    return isCouponExpired(coupon)
      ? t(keys.discounts.expiredOn, { date: dateStr })
      : t(keys.discounts.validTill, { date: dateStr });
  }
  return t(keys.discounts.scheduleLive);
}
